import React, { useState, useEffect, useRef } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { useAuthState, useAuthDispatch } from "context";
import { apiFetch, formatUiErrors } from "utils";
import Button from "components/Button";
import FormField from "components/FormField";
import DatePickerField from "components/DatePickerField";
import ConfirmationModal from "components/ConfirmationModal";
import AlertModal from "components/AlertModal";
import Pagination from "components/Pagination";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExclamationTriangle,
  faCheckCircle,
} from "@fortawesome/free-solid-svg-icons";
import moment from "moment";

const CleanupSchema = Yup.object().shape({
  organization_id: Yup.number()
    .required("Organization ID is required")
    .positive()
    .integer(),
  from_date: Yup.string(),
});

const PAGE_SIZE = 25;

const MergedDuplicateEventsCleanup = () => {
  const { token } = useAuthState();
  const dispatch = useAuthDispatch();

  const [previewStatus, setPreviewStatus] = useState(null);
  const [previewCount, setPreviewCount] = useState(null);
  const [previewRecords, setPreviewRecords] = useState([]);
  const [previewEvents, setPreviewEvents] = useState([]);
  const [previewOrg, setPreviewOrg] = useState(null);
  const [previewFromDate, setPreviewFromDate] = useState(null);
  const [isExecuted, setIsExecuted] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);

  const [recordsPage, setRecordsPage] = useState(1);
  const [eventsPage, setEventsPage] = useState(1);

  const pollingRef = useRef(null);
  const isMountedRef = useRef(true);
  const currentJobIdRef = useRef(null);
  const completedRef = useRef(false);

  const stopPolling = () => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  };

  const startPolling = (jobId) => {
    stopPolling();
    currentJobIdRef.current = jobId;
    completedRef.current = false;

    const fetchStatus = async () => {
      if (completedRef.current) return;
      if (!isMountedRef.current || currentJobIdRef.current !== jobId) return;

      try {
        const res = await apiFetch({
          token,
          dispatch,
          method: "GET",
          endpoint: `/api/internal/admin/cleanup_merged_duplicate_events/preview_status/${jobId}`,
        });

        const data = res.data;

        if (!isMountedRef.current || currentJobIdRef.current !== jobId) return;

        if (data.status === "completed") {
          completedRef.current = true;
          stopPolling();
          setPreviewStatus("completed");
          setPreviewCount(data.count);
          setPreviewRecords(data.records || []);
          setPreviewEvents(data.events || []);
          setRecordsPage(1);
          setEventsPage(1);
          setIsLoadingPreview(false);
          toast.success(
            `Preview completed: ${data.count} orphaned animal(s) found`
          );
        } else if (data.status === "failed") {
          completedRef.current = true;
          stopPolling();
          setPreviewStatus("failed");
          setIsLoadingPreview(false);
          toast.error(`Preview failed: ${data.error || "Unknown error"}`);
        } else {
          setPreviewStatus(data.status);
        }
      } catch {
        if (isMountedRef.current && currentJobIdRef.current === jobId) {
          completedRef.current = true;
          stopPolling();
          setPreviewStatus("failed");
          setIsLoadingPreview(false);
          toast.error("Error fetching preview status. Please try again.");
        }
      }
    };

    fetchStatus();
    pollingRef.current = setInterval(fetchStatus, 2000);
  };

  const handlePreview = async (values) => {
    setIsLoadingPreview(true);
    setPreviewStatus(null);
    setPreviewCount(null);
    setPreviewRecords([]);
    setPreviewEvents([]);
    setIsExecuted(false);

    const fromDate = values.from_date
      ? moment(values.from_date).startOf("month").format("YYYY-MM-DD")
      : null;

    setPreviewOrg(values.organization_id);
    setPreviewFromDate(fromDate);

    try {
      const res = await apiFetch({
        token,
        dispatch,
        method: "POST",
        endpoint: "/api/internal/admin/cleanup_merged_duplicate_events/preview",
        body: {
          organization_id: values.organization_id,
          from_date: fromDate,
        },
      });

      const jobId = res.data?.jobId;
      startPolling(jobId);
    } catch (error) {
      toast.error(formatUiErrors(error));
      setIsLoadingPreview(false);
    }
  };

  const handleExecute = async () => {
    try {
      await apiFetch({
        token,
        dispatch,
        method: "POST",
        endpoint: "/api/internal/admin/cleanup_merged_duplicate_events",
        body: {
          organization_id: previewOrg,
          from_date: previewFromDate,
        },
      });
      setIsExecuted(true);
      toast.success("Cleanup job queued successfully!");
    } catch (error) {
      toast.error(formatUiErrors(error));
    }
  };

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      stopPolling();
    };
  }, []);

  const paginatedRecords = previewRecords.slice(
    (recordsPage - 1) * PAGE_SIZE,
    recordsPage * PAGE_SIZE
  );
  const totalRecordsPages = Math.ceil(previewRecords.length / PAGE_SIZE);

  const paginatedEvents = previewEvents.slice(
    (eventsPage - 1) * PAGE_SIZE,
    eventsPage * PAGE_SIZE
  );
  const totalEventsPages = Math.ceil(previewEvents.length / PAGE_SIZE);

  return (
    <div className="mx-auto p-6">
      <h1 className="text-2xl font-bold mb-2">
        ShelterLuv — Orphaned Animals Cleanup
      </h1>
      <p className="text-gray-600 mb-6">
        This tool removes <code>animal_events</code> records and the associated
        animal from the <code>animals</code> table for animals that are no
        longer returned by the ShelterLuv API (merged or deleted animals).
        Re-aggregation will be required after cleanup.
      </p>

      <div className="bg-white rounded p-6 border shadow-sm mb-6 max-w-xl">
        <Formik
          initialValues={{ organization_id: "", from_date: "" }}
          validationSchema={CleanupSchema}
          onSubmit={handlePreview}
        >
          {({ isValid, dirty }) => (
            <Form>
              <div className="mb-4">
                <FormField
                  name="organization_id"
                  label="Organization ID"
                  type="number"
                  placeholder="e.g. 5365"
                />
              </div>
              <div className="mb-6">
                <DatePickerField
                  label="Detected From (Month/Year) — optional"
                  name="from_date"
                  dateFormat="MMMM yyyy"
                  showMonthYearPicker
                  className="form-input w-full"
                />
              </div>
              <Button
                type="submit"
                disabled={!isValid || !dirty || isLoadingPreview}
                isLoading={isLoadingPreview}
                loadingText="Loading..."
              >
                Preview
              </Button>
            </Form>
          )}
        </Formik>
      </div>

      {previewStatus === "completed" && !isExecuted && (
        <div className="bg-yellow-50 border border-yellow-200 rounded p-6 mb-6">
          <div className="flex items-start text-yellow-800 mb-4">
            <FontAwesomeIcon
              icon={faExclamationTriangle}
              className="mr-3 text-xl mt-1"
            />
            <div>
              <p className="font-bold text-lg mb-2">
                Preview — Organization {previewOrg}
              </p>
              <p className="text-sm mb-1">
                <strong>{previewCount}</strong> orphaned animal(s) detected
                {previewFromDate && (
                  <>
                    {" "}
                    since <strong>{previewFromDate}</strong>
                  </>
                )}
                .
              </p>
              <p className="text-sm font-semibold text-red-700 mt-3">
                This action is irreversible. Re-aggregation will be required
                after cleanup.
              </p>
            </div>
          </div>

          {previewRecords.length > 0 && (
            <div className="mt-4 overflow-x-auto">
              <p className="text-sm font-semibold text-yellow-900 mb-2">
                Orphaned Animals ({previewRecords.length}) — showing{" "}
                {(recordsPage - 1) * PAGE_SIZE + 1}–
                {Math.min(recordsPage * PAGE_SIZE, previewRecords.length)}
              </p>
              <table className="min-w-full text-xs border border-yellow-200 rounded">
                <thead className="bg-yellow-100 text-yellow-900">
                  <tr>
                    <th className="px-3 py-2 text-left">ID</th>
                    <th className="px-3 py-2 text-left">Animal ID</th>
                    <th className="px-3 py-2 text-left">Internal ID</th>
                    <th className="px-3 py-2 text-left">Detected At</th>
                    <th className="px-3 py-2 text-left">Events to Delete</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedRecords.map((record) => (
                    <tr
                      key={record.id}
                      className="border-t border-yellow-200 odd:bg-white even:bg-yellow-50"
                    >
                      <td className="px-3 py-2">{record.id}</td>
                      <td className="px-3 py-2">{record.animalId}</td>
                      <td className="px-3 py-2">{record.internalId}</td>
                      <td className="px-3 py-2">
                        {moment(record.detectedAt).format("MM/DD/YYYY HH:mm")}
                      </td>
                      <td className="px-3 py-2">{record.eventsToDelete}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {totalRecordsPages > 1 && (
                <div className="flex justify-end mt-3">
                  <Pagination
                    totalPages={totalRecordsPages}
                    currentPage={recordsPage}
                    onPageChanged={(p) => setRecordsPage(p)}
                  />
                </div>
              )}
            </div>
          )}

          {previewEvents.length > 0 && (
            <div className="mt-6 overflow-x-auto">
              <p className="text-sm font-semibold text-yellow-900 mb-2">
                Animal Events to be Deleted ({previewEvents.length}) — showing{" "}
                {(eventsPage - 1) * PAGE_SIZE + 1}–
                {Math.min(eventsPage * PAGE_SIZE, previewEvents.length)}
              </p>
              <table className="min-w-full text-xs border border-yellow-200 rounded">
                <thead className="bg-yellow-100 text-yellow-900">
                  <tr>
                    <th className="px-3 py-2 text-left">AE ID</th>
                    <th className="px-3 py-2 text-left">Animal ID</th>
                    <th className="px-3 py-2 text-left">Event ID</th>
                    <th className="px-3 py-2 text-left">Year</th>
                    <th className="px-3 py-2 text-left">Month</th>
                    <th className="px-3 py-2 text-left">Event Type</th>
                    <th className="px-3 py-2 text-left">Subtype</th>
                    <th className="px-3 py-2 text-left">Intake/Outcome Type</th>
                    <th className="px-3 py-2 text-left">Species</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedEvents.map((event) => (
                    <tr
                      key={event.id}
                      className="border-t border-yellow-200 odd:bg-white even:bg-yellow-50"
                    >
                      <td className="px-3 py-2">{event.id}</td>
                      <td className="px-3 py-2">{event.animalId}</td>
                      <td className="px-3 py-2">{event.eventId}</td>
                      <td className="px-3 py-2">{event.yearOfRecord}</td>
                      <td className="px-3 py-2">{event.monthOfRecord}</td>
                      <td className="px-3 py-2">{event.eventType}</td>
                      <td className="px-3 py-2">{event.subtype || "—"}</td>
                      <td className="px-3 py-2">
                        {event.intakeOutcomeType || "—"}
                      </td>
                      <td className="px-3 py-2">{event.species}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {totalEventsPages > 1 && (
                <div className="flex justify-end mt-3">
                  <Pagination
                    totalPages={totalEventsPages}
                    currentPage={eventsPage}
                    onPageChanged={(p) => setEventsPage(p)}
                  />
                </div>
              )}
            </div>
          )}

          {previewEvents.length === 0 && previewCount > 0 && (
            <p className="text-sm text-yellow-700 mt-4">
              No animal events found for these orphaned animals.
            </p>
          )}

          <div className="mt-6">
            <Button
              onClick={() => setIsModalOpen(true)}
              disabled={previewCount === 0}
            >
              Execute Cleanup for Org {previewOrg}
            </Button>
            {previewCount === 0 && (
              <p className="text-sm text-green-700 mt-2">
                No orphaned animals found for this organization.
              </p>
            )}
          </div>
        </div>
      )}

      {isExecuted && (
        <div className="bg-green-50 border border-green-200 rounded p-6">
          <div className="flex items-center text-green-700">
            <FontAwesomeIcon icon={faCheckCircle} className="mr-3 text-xl" />
            <div>
              <p className="font-bold">Cleanup job queued successfully!</p>
              <p className="text-sm">
                {previewCount} orphaned animal(s) and their associated
                AnimalEvent records were deleted for org {previewOrg}. Please
                re-aggregate the affected months.
              </p>
            </div>
          </div>
        </div>
      )}

      {previewStatus === "failed" && (
        <div className="bg-red-50 border border-red-200 rounded p-6">
          <p className="text-sm text-red-700">
            Preview failed. Please try again.
          </p>
        </div>
      )}

      {isModalOpen && (
        <AlertModal
          isModalOpen={isModalOpen}
          closeModal={() => setIsModalOpen(false)}
        >
          <ConfirmationModal
            prompt={`Are you sure you want to delete ${previewCount} orphaned animal(s) and their associated animal events for org ${previewOrg}? This action is irreversible and re-aggregation will be required afterward.`}
            closeModal={() => setIsModalOpen(false)}
            confirmModal={() => {
              setIsModalOpen(false);
              handleExecute();
            }}
          />
        </AlertModal>
      )}
    </div>
  );
};

export default MergedDuplicateEventsCleanup;
